import './AssessmentPromptNumber.scss';

import { ResponseType } from '@amzn/aws-assessment-template-management-service-typescript-client';
import { AssessmentResponseNumber, AssessmentResponseTypes } from '@amzn/awscat-aws-assessment-service-typescript-client';
import { AuthContextInterface, withAuthContext, withFlashContext } from '@amzn/awscat-react-components';
import { useMutation } from '@apollo/client';
import { FunctionComponent, useCallback, useState } from 'react';

import a2sApolloClient from '../../../../../api/a2s/ApolloClient';
import { CREATE_OR_UPDATE_ASSESSMENT_RESPONSES } from '../../../../../api/a2s/ApolloQueries';
import { AppLabelsContextInterface, withAppLabelsContext } from '../../../../../common/AppLabelsContext';
import rumClient from '../../../../../common/monitoring/RumClient';
import { TemplatePromptViewModel } from '../../../../administration/manage-templates/edit-template/TemplateModels';
import UpdateStatusIndicator from '../../../../common/UpdateStatusIndicator';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { assessmentIsReadOnly, generateAssessmentIdPromptId } from '../../../Utils';
import { createOrUpdateCurrentPromptResponseSuccessful, updateAssessmentPromptBeingUpdatedSuccessful } from '../../CurrentAssessmentSlice';

interface AssessmentPromptNumberState {
    id: string | null;
    value: string | null;
    responseSaved: boolean;
}

const initialAssessmentPromptNumberState: AssessmentPromptNumberState = {
    id: null,
    value: null,
    responseSaved: false,
};

type AssessmentPromptNumberProps = AppLabelsContextInterface &
    AuthContextInterface & {
        shouldDisplayTemplate: boolean;
    };

const AssessmentPromptNumber: FunctionComponent<AssessmentPromptNumberProps> = ({ auth, appLabels, shouldDisplayTemplate }): JSX.Element | null => {
    const dispatch = useAppDispatch();
    const currentAssessmentOrSelectedSnapshot = useAppSelector((state) => state.currentAssessmentState.currentAssessmentOrSelectedSnapshot);
    const currentPrompt = useAppSelector((state) => state.currentAssessmentState.currentPromptState.currentPrompt);
    const promptIdBeingUpdated = useAppSelector((state) => state.currentAssessmentState.promptIdBeingUpdated);
    const promptIndexBeingUpdated = useAppSelector((state) => state.currentAssessmentState.promptIndexBeingUpdate);
    const myUserId = auth?.getUserInfo()?.userId;
    const assessmentId = useAppSelector((state) => state.currentAssessmentState.currentAssessmentId);
    const promptId = currentPrompt?.id ?? '';
    const promptIndex = currentPrompt?.index;
    const responseType = currentPrompt?.responseType ?? null;
    const response = currentPrompt?.response ?? null;
    const placeholder = currentPrompt?.hint ?? null;

    // Template state
    const currentTemplatePrompt: TemplatePromptViewModel = useAppSelector((state) => state.currentTemplateState.currentPromptState.currentPrompt);
    const templateResponseType: ResponseType = currentTemplatePrompt?.responseType;

    const isReadOnly = shouldDisplayTemplate ? true : assessmentIsReadOnly(myUserId, currentAssessmentOrSelectedSnapshot, assessmentId);

    const [createOrUpdateAssessmentResponses, { loading, error }] = useMutation(CREATE_OR_UPDATE_ASSESSMENT_RESPONSES, {
        client: a2sApolloClient,
        onCompleted: (data) => {
            const responses = data.createOrUpdateAssessmentResponses;
            const updatedResponse = responses[0];
            if (updatedResponse) {
                dispatch(createOrUpdateCurrentPromptResponseSuccessful({ promptId: promptIdBeingUpdated, response: updatedResponse }));
                setState({ ...state, responseSaved: true });
            }
        },
    });

    const assessmentIdPromptId = generateAssessmentIdPromptId(assessmentId, promptId);

    const [state, setState] = useState<AssessmentPromptNumberState>(initialAssessmentPromptNumberState);

    const updateStatusIndicator = useCallback(() => {
        return (
            <UpdateStatusIndicator
                loading={loading}
                loadingText={appLabels.assessment.facilitate.updating_response}
                updateConfirmationText={
                    !loading && !error && state.responseSaved ? `Q${promptIndexBeingUpdated} ${appLabels.assessment.facilitate.response_saved}` : null
                }
                errorMessageSummary={!loading && !!error ? appLabels.assessment.facilitate.error_api : null}
                errorMessageDetail={error?.message ? `[Q${promptIndexBeingUpdated}:${error?.message}]` : null}
                tryAgainText={appLabels.assessment.facilitate.try_update_again}
                // TODO: Add the logic for try again action: SIM: https://issues.amazon.com/issues/A2T-OE-49
                tryAgainAction={() => {
                    rumClient.recordError('Trying again..');
                }}
            />
        );
    }, [
        loading,
        appLabels.assessment.facilitate.updating_response,
        appLabels.assessment.facilitate.response_saved,
        appLabels.assessment.facilitate.error_api,
        appLabels.assessment.facilitate.try_update_again,
        error,
        state.responseSaved,
        promptIndexBeingUpdated,
    ]);

    const getResponseValue = useCallback(() => {
        switch (responseType) {
            case AssessmentResponseTypes.NUMBER:
                return (response as AssessmentResponseNumber)?.intValue;
            default:
                return null;
        }
    }, [response, responseType]);

    const initialResponseValue = getResponseValue();

    const updateValue = useCallback(() => {
        if (isReadOnly) {
            return;
        }
        dispatch(updateAssessmentPromptBeingUpdatedSuccessful({ promptIdBeingUpdated: promptId, promptIndexBeingUpdate: promptIndex }));
        let numberValue = parseInt(state.value);
        // set to null to remove comment for all nullish or empty values. e.g. ''
        if (isNaN(numberValue)) {
            numberValue = null;
        }
        if (numberValue !== initialResponseValue) {
            createOrUpdateAssessmentResponses({
                variables: {
                    input: {
                        assessmentId: assessmentId,
                        responses: [
                            {
                                promptId,
                                numberValue,
                            },
                        ],
                    },
                },
            });
        }
    }, [isReadOnly, dispatch, promptId, promptIndex, state.value, initialResponseValue, createOrUpdateAssessmentResponses, assessmentId]);

    if (assessmentIdPromptId && state.id !== assessmentIdPromptId) {
        // Prompt or assessment has changed, update state
        setState({
            ...initialAssessmentPromptNumberState,
            id: assessmentIdPromptId,
            value: isNaN(initialResponseValue) ? '' : `${initialResponseValue}`,
        });
        return null;
    }

    if (shouldDisplayTemplate ? templateResponseType === ResponseType.Number : responseType === AssessmentResponseTypes.NUMBER) {
        return (
            <div className='awscat-assessment-number-input'>
                <input
                    placeholder={placeholder?.text ?? ''}
                    type='number'
                    name='number-input'
                    readOnly={isReadOnly}
                    value={state.value}
                    onChange={(detail) => setState({ ...state, value: detail.target.value })}
                    onBlur={updateValue}
                    data-testid='number-input'
                />
                {updateStatusIndicator()}
            </div>
        );
    }

    return null;
};

export default withAuthContext(withAppLabelsContext(withFlashContext(AssessmentPromptNumber)));
